import {
  appRoutes,
  commonButtonMessages,
  createDta,
  DtaAccountField,
  fiduciarieMessages,
  fiduciarieSliceActions,
  mapDtaAccountError,
  useAppDispatch,
  validateDtaAccountRequest
} from 'azshared';
import type { DtaAccount, DtaAccountError, DtaAccountFieldErrors, DtaAccountRequest } from 'azshared';
import { FormEvent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AppBreadcrumb } from '../../../../components/breadcrumb/app-breadcrumb';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../../components/buttons/app-button';
import { Card, CardSize } from '../../../../components/card/card';
import { AppInputText } from '../../../../components/input-text/app-input-text';
import { ModalDialog } from '../../../../components/modal/modal-dialog';
import { useGlobalActions } from '../../../../hooks/global-actions';
import { BreadcrumbItem, mapBreadcrumb } from '../../../../utils/breadcrumb-item';
import { AccountField, accountFieldGroups } from '../fiduciarie-account-fields';
import { formatFiduciarieFieldError } from '../fiduciarie-field-error';
import './fiduciarie-create-page.scss';

type CreateForm = Partial<Record<DtaAccountField, string>>;

const toDtaAccountRequest = (form: CreateForm): DtaAccountRequest => {
  const value = (key: DtaAccountField) => form[key]?.trim() ?? '';
  const codiceFiscale = value(DtaAccountField.TrustCompanyCodiceFiscale);
  const partitaIva = value(DtaAccountField.TrustCompanyPartitaIva);
  return {
    contact: {
      phone: value(DtaAccountField.ContactPhone),
      email: value(DtaAccountField.ContactEmail)
    },
    trustCompany: {
      name: value(DtaAccountField.TrustCompanyName),
      code: value(DtaAccountField.TrustCompanyCode),
      agent: value(DtaAccountField.TrustCompanyAgent),
      // Only one of the two is sent, the codice fiscale first if both were somehow filled.
      ...(codiceFiscale ? { codiceFiscale } : partitaIva && { partitaIva })
    },
    actingPerson: {
      name: value(DtaAccountField.ActingPersonName),
      surname: value(DtaAccountField.ActingPersonSurname),
      codiceFiscale: value(DtaAccountField.ActingPersonCodiceFiscale)
    },
    addressBook: value(DtaAccountField.AddressBook)
  };
};

export const FiduciarieCreatePage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast, closeToast } = useGlobalActions();

  const breadcrumbItems: BreadcrumbItem[] = [
    mapBreadcrumb['gestione-fiduciarie'],
    {
      key: 'creazione',
      label: fiduciarieMessages.breadcrumbCreate,
      path: appRoutes.AdminFiduciarieCreate
    }
  ];

  const [form, setForm] = useState<CreateForm>({});
  const [fieldErrors, setFieldErrors] = useState<DtaAccountFieldErrors>({});
  // Kept only in the page state while the credentials modal is open: the temporary password is shown once.
  const [createdAccount, setCreatedAccount] = useState<DtaAccount>();

  const handleChange = useCallback((field: AccountField, value?: string) => {
    const { key, exclusiveWith } = field;
    const clearedKey = exclusiveWith && value?.trim() ? exclusiveWith : undefined;
    setForm((current) => ({
      ...current,
      [key]: field.uppercase ? value?.toUpperCase() : value,
      ...(clearedKey && { [clearedKey]: undefined })
    }));
    // The error of an edited (or cleared) field is stale: it is checked again on the next submit.
    setFieldErrors((current) => Object.fromEntries(Object.entries(current).filter(([errorKey]) => errorKey !== key && errorKey !== clearedKey)));
  }, []);

  const isDisabled = (field: AccountField) => !!field.exclusiveWith && !!form[field.exclusiveWith]?.trim();

  const handleCancel = useCallback(() => {
    navigate(appRoutes.AdminFiduciarie);
  }, [navigate]);

  const handleCloseCreatedAccount = useCallback(() => {
    setCreatedAccount(undefined);
    navigate(appRoutes.AdminFiduciarie);
  }, [navigate]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      const request = toDtaAccountRequest(form);
      const validationErrors = validateDtaAccountRequest(request);
      setFieldErrors(validationErrors);
      if (Object.values(validationErrors).some((error) => error === 'required' || error === 'fiscalDataRequired')) {
        showToast({
          type: 'error',
          title: formatMessage(fiduciarieMessages.errorRequiredFields),
          close: closeToast
        });
      }
      if (Object.keys(validationErrors).length) {
        return;
      }

      dispatch(createDta(request))
        .unwrap()
        .then(({ data: account }) => setCreatedAccount(account))
        .catch((error: DtaAccountError) => {
          // The rejected fields are highlighted on the form, the toast only reports the failure.
          setFieldErrors(mapDtaAccountError(error));
          showToast({
            type: 'error',
            title: formatMessage(fiduciarieMessages.createError),
            close: closeToast
          });
        })
        // The response holds the temporary password: drop it from the store, the modal shows it from the page state.
        .finally(() => dispatch(fiduciarieSliceActions.resetCreateDtaState()));
    },
    [form, dispatch, formatMessage, showToast, closeToast]
  );

  const createdAccountRows = createdAccount
    ? [
        { label: fiduciarieMessages.createdUsernameLabel, value: createdAccount.username },
        { label: fiduciarieMessages.createdTemporaryPasswordLabel, value: createdAccount.temporaryPassword },
        {
          label: fiduciarieMessages.createdAccountHolderLabel,
          value: `${createdAccount.actingPerson.name} ${createdAccount.actingPerson.surname}`
        },
        {
          label: fiduciarieMessages.createdTrustCompanyLabel,
          value: `${createdAccount.trustCompany.code} - ${createdAccount.trustCompany.name}`
        }
      ]
    : [];

  return (
    <div className='fiduciarie-create-page'>
      <div className='fiduciarie-create-card'>
        <AppBreadcrumb customItems={breadcrumbItems} />
        <Card title={formatMessage(fiduciarieMessages.pageTitle)} sizeVariant={CardSize.MAXI}>
          <form className='fiduciarie-form' onSubmit={handleSubmit}>
            <div className='card-form-title'>{formatMessage(fiduciarieMessages.cardTitleNew)}</div>
            {accountFieldGroups.map((group) => (
              <fieldset key={group.key} className='fiduciarie-form-section'>
                <legend className='fiduciarie-form-section-title'>{formatMessage(group.title)}</legend>
                <div className='fiduciarie-form-grid'>
                  {group.fields.map((field) => (
                    <AppInputText
                      key={field.key}
                      value={form[field.key] ?? ''}
                      onChange={(value) => handleChange(field, value)}
                      inputMode={field.inputMode}
                      maxLength={field.maxLength}
                      disabled={isDisabled(field)}
                      // every field is required, except the codice fiscale and partita IVA: one of them is enough
                      required={!field.exclusiveWith}
                      trimSpaces={false}
                      label={formatMessage(field.label)}
                      placeholder={formatMessage(field.placeholder)}
                      errorMessage={formatFiduciarieFieldError(formatMessage, fieldErrors[field.key])}
                    />
                  ))}
                </div>
              </fieldset>
            ))}

            <div className='form-buttons-row'>
              <AppButton
                type={AppButtonType.BUTTON}
                variant={AppButtonVariant.Secondary}
                label={formatMessage(fiduciarieMessages.buttonCancel)}
                onClick={handleCancel}
              />
              <AppButton type={AppButtonType.SUBMIT} label={formatMessage(fiduciarieMessages.buttonConfirm)} />
            </div>
          </form>
        </Card>
      </div>

      <ModalDialog
        open={!!createdAccount}
        title={formatMessage(fiduciarieMessages.createSuccess)}
        subtitle={formatMessage(fiduciarieMessages.createSuccessModalSubtitle)}
        confirmButtonLabel={formatMessage(commonButtonMessages.ok)}
        onClose={handleCloseCreatedAccount}
        onConfirm={handleCloseCreatedAccount}
        onCancel={handleCloseCreatedAccount}>
        <dl className='fiduciarie-created-account'>
          {createdAccountRows.map(({ label, value }) => (
            <div key={label.id} className='created-account-row'>
              <dt>{formatMessage(label)}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </ModalDialog>
    </div>
  );
};
